export type TProfileBanner = {
  id: number;
  name: string;
  asset_name: string;
  allow_character: boolean;
  allow_group: boolean;
  allow_horde: boolean;
  allow_alliance: boolean;
  allow_mixed: boolean;
  min_prestige_level: number;
  required_feature_checks?: FeatureBannerCheck[];
  required_animated_version_feature_checks?: FeatureBannerCheck[];
  has_animated_version?: boolean;
  grant_feature_when_applied?: string[];
  grant_feature_when_applied_with_animation?: string[];
  is_animated?: boolean; // should not be manually set; just returned dynamically
};

export type FeatureBannerCheck = {
  feature: string;
  // if description is not provided, the UI will not show a reason why the banner is unavailable
  description?: string;
  label?: string;
  required?: 'patron';
  // restricts the check to one customization context; omitted = applies in all contexts.
  // Keeps guild-perk checks out of character pickers and vice versa.
  applies_to?: 'character' | 'group';
};

export type TProfileFrame = {};

/** A curated page-background option for guild pages (no user uploads). */
export type TPageBackdrop = {
  id: number;
  name: string;
  asset_name: string;
  required_feature_checks?: FeatureBannerCheck[];
};

/**
 * The checks that apply in a customization context. Single source of the
 * `applies_to` semantics — used by both the save-path validation (backend)
 * and the picker lock display (frontend) so they can never disagree.
 */
export function getApplicableFeatureChecks(
  checks: FeatureBannerCheck[] | undefined,
  context: 'character' | 'group'
): FeatureBannerCheck[] {
  return (checks || []).filter((check) => !check.applies_to || check.applies_to === context);
}

// Bump when a page_backdrop asset is replaced in place (e.g. re-exported or
// resized) so the CDN/browser drops the stale copy. Filenames are stable, so
// without this a swapped image keeps serving the old bytes.
export const PAGE_BACKDROP_ASSET_VERSION = 19;

/** Asset path convention shared by the settings picker and the guild page render. */
export function getPageBackdropAssetPath(assetName: string): string {
  return `/images/profile/page_backdrops/${assetName}.jpg?v=${PAGE_BACKDROP_ASSET_VERSION}`;
}

// All animated banners will be exactly this ID above its non-animated version.
// That way we can know if a banner is animated just from looking at banner,
// and also return it back to a base version with that same ID.
export const ANIMATED_BANNER_BASE_ID = 100000;

// Canonical brand-color shape: a 6-digit hex color like #1a2b3c. Kept here so the
// same regex backs both frontend picker validation and server-side save-path
// sanitization (defense-in-depth independent of route Joi).
const BRAND_COLOR_HEX = /^#[0-9a-fA-F]{6}$/;

/** True when value is a syntactically valid 6-digit hex brand color (whitespace tolerated). */
export function isBrandColor(value: unknown): value is string {
  return typeof value === 'string' && BRAND_COLOR_HEX.test(value.trim());
}

/**
 * Normalize a valid brand color to lowercased #rrggbb. Callers must gate on
 * isBrandColor first; passing an invalid value yields a lowercased trim, not a guarantee.
 */
export function normalizeBrandColor(value: string): string {
  return value.trim().toLowerCase();
}
